import { MANDALA_GATE_SEQUENCE_CCW } from '../mandala.constants';
import type { HdLocksKeysPeriodId, HdLocksKeysStarGroup } from './hd-locks-keys-metadata';

/**
 * Global Cycles-tabel ("The Global Consciousness Program", 16513 BC – 3674 AD)
 * — mechanisch gegenereerd in plaats van overgetypt, naar het referentiebeeld
 * uit het bronmateriaal (Master cycles / Global Cycles.png).
 *
 * Generatieprincipes (geverifieerd tegen het bronbeeld én de wielgeometrie):
 * - Elke key-rij K (1..7) van een blok is exact de lock-rij van dat blok,
 *   K posities retrograde verschoven in de wielvolgorde
 *   ({@link MANDALA_GATE_SEQUENCE_CCW}).
 * - De linkerkolommen van blok N zijn de rechterkolommen van blok N−1 (de
 *   "ademhaling van de Monopool": Sphinx en Vessel wisselen per blok van kant).
 * - Kruisnamen hangen enkel af van (lock-kruis, shift) en zijn dus in elk blok
 *   identiek per rijpositie.
 * - Elk viertal staat in de notatie van een Right Angle Cross: Persoonlijkheid
 *   Zon/Aarde | Design Zon/Aarde, dus [x, x+180°, x−90°, x+90°]. Kolom 1 links
 *   is altijd het lentepunt zelf; het variantnummer (1-4) is het kwartaal van
 *   de poort in kolom 1.
 * - Een kruis dat de wijzer afgeeft, krijgt hem pas twee locks (90°) later
 *   terug: in het volgende blok staat het rechts een kwartslag gedraaid,
 *   [a,b,c,d] → [c,d,b,a]. De andere kant schuift ongewijzigd van rechts naar
 *   links.
 * - Tijdlijn: rijen van exact 411 jaar; BC telt af (volgende start = einde − 1),
 *   AD telt op, met de BC→AD-overgangsrij "33 BC-378 AD" (geen jaar nul).
 *
 * Twee rekenfouten uit het bronbeeld zijn hier stilzwijgend gecorrigeerd:
 * rij 2 van blok 1 eindigt op 15690 (bron: "15688") en de lock-rij van blok 5
 * loopt tot 2918 met key-rij 1 vanaf 2917 (bron: "2928"/"2927") — de
 * 411-jaar-ritmiek laat geen andere waarden toe.
 */

export type HdGlobalCyclesBlockColor =
  | 'teal'
  | 'yellow'
  | 'green'
  | 'orange'
  | 'purple'
  | 'aqua'
  | 'blue';

export interface HdGlobalCyclesRow {
  readonly id: string;
  readonly yearLabel: string;
  readonly kind: 'lock' | 'key';
  readonly phaseLabel: 'The Lock' | 'The Key';
  readonly leftGates: readonly number[];
  readonly leftCross: string;
  readonly rightGates: readonly number[];
  readonly rightCross: string;
  /**
   * Lock-kruis waarvan links en rechts de sleutels staan. Links is altijd het
   * vierkant van het lentepunt, dus `leftGroup` wisselt bij elke lock-fase.
   */
  readonly leftGroup: HdLocksKeysStarGroup;
  readonly rightGroup: HdLocksKeysStarGroup;
  /** Variantnummer (1-4) van het kruis links en rechts: kwartaal van de poort in kolom 1. */
  readonly leftVariant: number;
  readonly rightVariant: number;
  /**
   * Enkel op een lock-rij vanaf blok 2: het viertal dat in het vorige blok
   * links stond en hier een kwartslag gedraaid rechts staat; null elders. De
   * linkerkant van zo'n rij is ongewijzigd doorgegeven van rechts.
   */
  readonly rightTurnedFrom: readonly number[] | null;
  readonly blockColor: HdGlobalCyclesBlockColor;
  /**
   * Locks & Keys-periode van deze rij: elke rij is een kiesbare periode. De
   * rijen met eigen content dragen een benoemde id ({@link NAMED_PERIOD_IDS}),
   * de andere hun rij-id.
   */
  readonly periodId: HdLocksKeysPeriodId;
  /**
   * Hoeveel wielposities elke key retrograde van zijn lock staat (1-7); 0 in
   * een lock-rij, waar de wijzer op de G-poorten zelf staat.
   */
  readonly keyShift: number;
  /** Kruisnaam van de Vessel of Love-viertal in deze rij. */
  readonly vesselCross: string;
  /** Kruisnaam van de Sphinx-viertal in deze rij. */
  readonly sphinxCross: string;
  /** Bron-highlight: de huidige periode-rij (1615-2026), wit uitgelicht. */
  readonly isCurrent: boolean;
}

const SPHINX_GATES: ReadonlySet<number> = new Set([1, 2, 7, 13]);

/** Lock-rij-volgorde (linkerkolommen) per blok, uit het bronbeeld. */
const BLOCK_LOCK_LEFT: readonly (readonly number[])[] = [
  [1, 2, 7, 13],
  [46, 25, 15, 10],
  [7, 13, 2, 1],
  [15, 10, 25, 46],
  [2, 1, 13, 7],
  [25, 46, 10, 15],
  [13, 7, 1, 2],
];

/** Rechterkolommen van het (enkel als lock-rij getoonde) laatste blok. */
const LAST_BLOCK_RIGHT: readonly number[] = [10, 15, 46, 25];

const BLOCK_COLORS: readonly HdGlobalCyclesBlockColor[] = [
  'teal', 'yellow', 'green', 'orange', 'purple', 'aqua', 'blue',
];

/** Key-kruisnamen per shift (1..7), identiek in elk blok. */
const SPHINX_KEY_CROSS_NAMES: readonly string[] = [
  'Cross of the Four Ways',
  'Cross of the Unexpected',
  'Cross of Laws',
  'Cross of Maya',
  'Cross of Penetration',
  'Cross of Tension',
  'Cross of Service',
];

const VESSEL_KEY_CROSS_NAMES: readonly string[] = [
  'Cross of Eden',
  'Cross of Rulership',
  'Cross of Consciousness',
  'Cross of Planning',
  'Cross of the Sleeping Phoenix',
  'Cross of Contagion',
  'Cross of Explanation',
];

const SPHINX_LOCK_CROSS_NAME = 'Cross of the Sphinx';
const VESSEL_LOCK_CROSS_NAME = 'Cross of the Vessel of Love';

/**
 * Rijen met eigen periode-content in de i18n-scope (`periods.<id>`,
 * `locks.<gate>.keys.<id>`); de id's leven ook in bewaarde settings, dus
 * nooit hernoemen.
 */
const NAMED_PERIOD_IDS: Readonly<Record<string, HdLocksKeysPeriodId>> = {
  '1615-2026 AD': 'planning',
  '2027-2438 AD': 'sleepingPhoenix',
  '2439-2850 AD': 'contagion',
  '2851-3262 AD': 'explanation',
};

const YEARS_PER_ROW = 411;
const TABLE_START_YEAR_BC = 16513;

const GATE_SEQUENCE_INDEX = new Map<number, number>(
  MANDALA_GATE_SEQUENCE_CCW.map((gate, index) => [gate, index]),
);

/** Poort op `shift` posities retrograde (tegen de lengtevolgorde in) van `gate`. */
function shiftRetrograde(gate: number, shift: number): number {
  const index = GATE_SEQUENCE_INDEX.get(gate) ?? 0;
  return MANDALA_GATE_SEQUENCE_CCW[(index - shift + MANDALA_GATE_SEQUENCE_CCW.length) % MANDALA_GATE_SEQUENCE_CCW.length];
}

function isSphinxFoursome(gates: readonly number[]): boolean {
  return SPHINX_GATES.has(gates[0]);
}

function foursomeGroup(lockGates: readonly number[]): HdLocksKeysStarGroup {
  return isSphinxFoursome(lockGates) ? 'sphinx' : 'vessel';
}

/** Kwartaal (1 Initiatie … 4 Mutatie) van de poort in kolom 1: het variantnummer van het kruis. */
function crossVariant(gates: readonly number[]): number {
  return Math.floor((GATE_SEQUENCE_INDEX.get(gates[0]) ?? 0) / 16) + 1;
}

function keyCrossName(lockGates: readonly number[], shift: number): string {
  return isSphinxFoursome(lockGates)
    ? SPHINX_KEY_CROSS_NAMES[shift - 1]
    : VESSEL_KEY_CROSS_NAMES[shift - 1];
}

function lockCrossName(lockGates: readonly number[]): string {
  return isSphinxFoursome(lockGates) ? SPHINX_LOCK_CROSS_NAME : VESSEL_LOCK_CROSS_NAME;
}

/** Doorlopende 411-jaar-tijdlijn met BC-aftelling, AD-optelling en de overgangsrij. */
interface TimelineCursor {
  era: 'BC' | 'AD';
  start: number;
}

function nextYearLabel(cursor: TimelineCursor): string {
  if (cursor.era === 'BC') {
    const end = cursor.start - YEARS_PER_ROW;
    if (end >= 1) {
      const label = `${cursor.start}-${end} BC`;
      cursor.start = end - 1;
      return label;
    }
    // Overgangsrij zonder jaar nul: 33 BC + 378 AD = 411 jaar.
    const adEnd = YEARS_PER_ROW - cursor.start;
    const label = `${cursor.start} BC-${adEnd} AD`;
    cursor.era = 'AD';
    cursor.start = adEnd + 1;
    return label;
  }
  const end = cursor.start + YEARS_PER_ROW;
  const label = `${cursor.start}-${end} AD`;
  cursor.start = end + 1;
  return label;
}

function buildRows(): readonly HdGlobalCyclesRow[] {
  const rows: HdGlobalCyclesRow[] = [];
  const cursor: TimelineCursor = { era: 'BC', start: TABLE_START_YEAR_BC };

  for (let block = 0; block < BLOCK_LOCK_LEFT.length; block++) {
    const left = BLOCK_LOCK_LEFT[block];
    const right = block + 1 < BLOCK_LOCK_LEFT.length ? BLOCK_LOCK_LEFT[block + 1] : LAST_BLOCK_RIGHT;
    const color = BLOCK_COLORS[block];
    const isLastBlock = block === BLOCK_LOCK_LEFT.length - 1;

    const lockYearLabel = nextYearLabel(cursor);
    const lockRowId = `block-${block}-lock`;
    rows.push({
      id: lockRowId,
      yearLabel: lockYearLabel,
      kind: 'lock',
      phaseLabel: 'The Lock',
      leftGates: left,
      leftCross: lockCrossName(left),
      rightGates: right,
      rightCross: lockCrossName(right),
      leftGroup: foursomeGroup(left),
      rightGroup: foursomeGroup(right),
      leftVariant: crossVariant(left),
      rightVariant: crossVariant(right),
      rightTurnedFrom: block > 0 ? BLOCK_LOCK_LEFT[block - 1] : null,
      blockColor: color,
      periodId: NAMED_PERIOD_IDS[lockYearLabel] ?? lockRowId,
      keyShift: 0,
      vesselCross: VESSEL_LOCK_CROSS_NAME,
      sphinxCross: SPHINX_LOCK_CROSS_NAME,
      isCurrent: false,
    });

    // Het laatste blok toont enkel zijn lock-rij (zoals in het bronbeeld).
    if (isLastBlock) {
      break;
    }

    for (let shift = 1; shift <= 7; shift++) {
      const yearLabel = nextYearLabel(cursor);
      const rowId = `block-${block}-key-${shift}`;
      const periodId = NAMED_PERIOD_IDS[yearLabel] ?? rowId;
      const leftIsSphinx = isSphinxFoursome(left);
      const leftGates = left.map(gate => shiftRetrograde(gate, shift));
      const rightGates = right.map(gate => shiftRetrograde(gate, shift));
      rows.push({
        id: rowId,
        yearLabel,
        kind: 'key',
        phaseLabel: 'The Key',
        leftGates,
        leftCross: keyCrossName(left, shift),
        rightGates,
        rightCross: keyCrossName(right, shift),
        leftGroup: foursomeGroup(left),
        rightGroup: foursomeGroup(right),
        leftVariant: crossVariant(leftGates),
        rightVariant: crossVariant(rightGates),
        rightTurnedFrom: null,
        blockColor: color,
        periodId,
        keyShift: shift,
        vesselCross: keyCrossName(leftIsSphinx ? right : left, shift),
        sphinxCross: keyCrossName(leftIsSphinx ? left : right, shift),
        isCurrent: periodId === 'planning',
      });
    }
  }
  return rows;
}

export const HD_GLOBAL_CYCLES_ROWS: readonly HdGlobalCyclesRow[] = buildRows();

export const HD_GLOBAL_CYCLES_TITLE = 'GLOBAL CYCLES: The Global Consciousness Program';
export const HD_GLOBAL_CYCLES_RANGE = 'From 16513 BC - 3674 AD';
